import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Plus, Trash2 } from "lucide-react";

/**
 * ConvergentPackageModal — Diálogo de creación/edición de paquetes convergentes.
 *
 * Extraído 1:1 desde `pages/CatalogPage.js`. Permite componer un paquete a partir de
 * servicios del catálogo (agrupados por categoría en el selector), expande los servicios
 * multi-línea (`unidades_por_seleccion`) y muestra una vista previa con el total calculado.
 *
 * Eventos: al confirmar dispara `handleSaveConvergent` (crea o actualiza según
 * `editingConvergent`); el cierre limpia el estado con `setConvergentModalOpen(false)`.
 *
 * @param {boolean}     props.convergentModalOpen        Apertura del diálogo.
 * @param {Function}    props.setConvergentModalOpen      Setter del flag de apertura.
 * @param {Object|null} props.editingConvergent           Paquete en edición (null = creación).
 * @param {Object}      props.convergentForm              Formulario { nombre, descripcion }.
 * @param {Function}    props.setConvergentForm           Setter del formulario.
 * @param {string[]}    props.convergentServiceIds        IDs de servicios seleccionados (con huecos "").
 * @param {Function}    props.setConvergentServiceIds     Setter de los IDs seleccionados.
 * @param {Array}       props.services                    Catálogo completo de servicios.
 * @param {Array}       props.convergentEligibleServices  Servicios activos elegibles para el paquete.
 * @param {Function}    props.formatCurrency              Formateador de moneda (EUR) del padre.
 * @param {Function}    props.handleSaveConvergent        Handler de guardado del paquete.
 */
export const ConvergentPackageModal = ({
  convergentModalOpen,
  setConvergentModalOpen,
  editingConvergent,
  convergentForm,
  setConvergentForm,
  convergentServiceIds,
  setConvergentServiceIds,
  services,
  convergentEligibleServices,
  formatCurrency,
  handleSaveConvergent,
}) => {
  return (
      <Dialog open={convergentModalOpen} onOpenChange={(open) => {
        if (!open) setConvergentModalOpen(false);
      }}>
        <DialogContent className="bg-[#0F0F3D] border-[#1E1E5F] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">
              {editingConvergent ? "Editar Paquete Convergente" : "Nuevo Paquete Convergente"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Nombre del paquete *</Label>
              <Input
                value={convergentForm.nombre}
                onChange={(e) => setConvergentForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder="Ej: Pack Fibra 300 + Móvil 25GB"
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                data-testid="convergent-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Descripción</Label>
              <Textarea
                value={convergentForm.descripcion}
                onChange={(e) => setConvergentForm(f => ({ ...f, descripcion: e.target.value }))}
                placeholder="Descripción opcional..."
                rows={2}
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
              />
            </div>

            <div className="border-t border-[#1E1E5F] pt-4">
              <Label className="text-[#03BEE4] font-semibold text-sm">Servicios que componen el paquete</Label>
              <p className="text-xs text-slate-500 mb-3">
                Selecciona los servicios del catálogo que compondrán el paquete. Los servicios multi-línea se expandirán automáticamente.
              </p>
              <div className="space-y-2">
                {convergentServiceIds.map((svcId, idx) => {
                  const svc = services.find(s => s.id === svcId);
                  return (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="flex-1">
                        <Select value={svcId} onValueChange={(v) => {
                          const n = [...convergentServiceIds];
                          n[idx] = v;
                          setConvergentServiceIds(n);
                        }}>
                          <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-9 text-sm" data-testid={`convergent-svc-select-${idx}`}>
                            <SelectValue placeholder="Selecciona servicio..." />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                            {(() => {
                              const grouped = {};
                              convergentEligibleServices.forEach(s => {
                                const cat = s.categoria || "Sin categoría";
                                if (!grouped[cat]) grouped[cat] = [];
                                grouped[cat].push(s);
                              });
                              return Object.entries(grouped).map(([cat, svcs]) => (
                                <React.Fragment key={cat}>
                                  <div className="px-2 py-1.5 text-xs text-[#03BEE4] font-semibold uppercase tracking-wider pointer-events-none">{cat}</div>
                                  {svcs.map(s => (
                                    <SelectItem key={s.id} value={s.id} className="text-white text-sm pl-4">
                                      {s.nombre} — {s.precio_paquete != null ? formatCurrency(s.precio_paquete) : formatCurrency(s.precio_base)} ({cat})
                                    </SelectItem>
                                  ))}
                                </React.Fragment>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      </div>
                      {svc && (
                        <span className="text-xs text-slate-400 min-w-[80px] text-right">
                          {formatCurrency(svc.precio_paquete != null ? svc.precio_paquete : svc.precio_base || 0)}
                          {svc.unidades_por_seleccion > 1 && <span className="text-blue-400 ml-1">x{svc.unidades_por_seleccion}</span>}
                        </span>
                      )}
                      <button
                        onClick={() => setConvergentServiceIds(convergentServiceIds.filter((_, j) => j !== idx))}
                        className="text-red-400 hover:text-red-300 p-1"
                        data-testid={`convergent-svc-remove-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConvergentServiceIds([...convergentServiceIds, ""])}
                  className="text-[#03BEE4] text-xs h-7"
                  data-testid="convergent-add-svc-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Añadir servicio
                </Button>
              </div>
            </div>

            {/* Preview */}
            {convergentServiceIds.some(id => id) && (
              <div className="bg-[#0A0A35] border border-[#1E1E5F] rounded-lg p-3">
                <p className="text-xs text-[#03BEE4] font-semibold mb-2">VISTA PREVIA</p>
                {convergentServiceIds.filter(id => id).map((id, idx) => {
                  const svc = services.find(s => s.id === id);
                  if (!svc) return null;
                  const svcPrice = svc.precio_paquete != null ? svc.precio_paquete : svc.precio_base || 0;
                  const units = svc.unidades_por_seleccion || 1;
                  if (units > 1) {
                    return Array.from({ length: units }, (_, i) => (
                      <div key={`${idx}-${i}`} className="flex justify-between text-sm py-0.5">
                        <span className="text-slate-300">{svc.nombre.replace(/^\(\d+x\)\s*/, "")} (L{i + 1})</span>
                        <span className="text-[#42E695]">{formatCurrency(svcPrice / units)}</span>
                      </div>
                    ));
                  }
                  return (
                    <div key={idx} className="flex justify-between text-sm py-0.5">
                      <span className="text-slate-300">{svc.nombre}</span>
                      <span className="text-[#42E695]">{formatCurrency(svcPrice)}</span>
                    </div>
                  );
                })}
                <div className="border-t border-[#1E1E5F] mt-2 pt-2 flex justify-between">
                  <span className="text-white font-semibold text-sm">Total:</span>
                  <span className="text-[#42E695] font-bold">
                    {formatCurrency(convergentServiceIds.filter(id => id).reduce((sum, id) => {
                      const svc = services.find(s => s.id === id);
                      return sum + (svc?.precio_paquete != null ? svc.precio_paquete : svc?.precio_base || 0);
                    }, 0))}
                  </span>
                </div>
              </div>
            )}

            <DialogFooter className="pt-4">
              <Button
                variant="outline"
                onClick={() => setConvergentModalOpen(false)}
                className="border-[#1E1E5F] text-slate-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveConvergent}
                className="bg-[#42E695] text-[#04042C]"
                data-testid="save-convergent-btn"
              >
                {editingConvergent ? "Guardar Cambios" : "Crear Paquete"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
  );
};
